/**
 * 系谱表业绩运维：清零 / 重算叠回 / 钉死 L/R。
 */
import { Alert, Button, Descriptions, Form, Input, InputNumber, Radio, Select, Space, Typography, message } from 'antd';
import { useState } from 'react';
import { api, ApiError } from '../api/client';

export type PerfOpsPreview = {
  user_id: number;
  username: string;
  action: 'zero' | 'restack' | 'pin';
  zero_scope?: string;
  affected_count: number;
  sample_users: Array<{ id: number; username: string; team_sum: string; my_sum: string }>;
  restack?: {
    scanned: number;
    updated: number;
    changes: Array<{
      id: number;
      username: string;
      before: string;
      after: string;
      skipped: boolean;
    }>;
  };
  pin?: {
    left: {
      child_id: number | null;
      child_username: string | null;
      before_display: string;
      after_display: string | null;
    };
    right: {
      child_id: number | null;
      child_username: string | null;
      before_display: string;
      after_display: string | null;
    };
  };
  note: string;
  would_apply: boolean;
};

type FormValues = {
  action: 'zero' | 'restack' | 'pin';
  zero_scope: 'self' | 'binary_subtree' | 'left_leg' | 'right_leg';
  preserve_pinned: 0 | 1;
  left_display?: number;
  right_display?: number;
  confirm_username: string;
  note: string;
};

export function PerfOpsForm({
  userId,
  username,
  onDone,
}: {
  userId: number;
  username: string;
  onDone?: () => void;
}) {
  const [form] = Form.useForm<FormValues>();
  const action = Form.useWatch('action', form) ?? 'zero';
  const [preview, setPreview] = useState<PerfOpsPreview | null>(null);
  const [pending, setPending] = useState<FormValues | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const endpoint = `/api/admin/users/${userId}/perf-ops`;

  async function handlePreview(values: FormValues) {
    setSubmitting(true);
    try {
      const body = {
        action: values.action,
        zero_scope: values.zero_scope,
        preserve_pinned: values.preserve_pinned === 1,
        left_display: values.left_display,
        right_display: values.right_display,
        confirm_username: values.confirm_username,
        note: values.note,
        xian: true,
      };
      const data = await api.post<PerfOpsPreview>(endpoint, body);
      setPreview(data);
      setPending(values);
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '预览失败');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleConfirm() {
    if (!pending) return;
    setConfirming(true);
    try {
      await api.post(endpoint, {
        action: pending.action,
        zero_scope: pending.zero_scope,
        preserve_pinned: pending.preserve_pinned === 1,
        left_display: pending.left_display,
        right_display: pending.right_display,
        confirm_username: pending.confirm_username,
        note: pending.note,
        xian: false,
      });
      message.success('业绩操作已完成');
      setPreview(null);
      setPending(null);
      form.resetFields();
      onDone?.();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '执行失败');
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Alert
        type="info"
        showIcon
        message="表上 L/R = 直挂子节点 team_sum÷1000。清零只清团队 team_sum（保留个人 my_sum）；重算叶节点 team=my，上级 my+Σ子；钉死改直挂子节点 team_sum。"
      />

      <Form
        form={form}
        layout="vertical"
        initialValues={{
          action: 'zero',
          zero_scope: 'binary_subtree',
          preserve_pinned: 1,
          confirm_username: '',
          note: '',
        }}
        onFinish={handlePreview}
      >
        <Form.Item name="action" label="操作类型" rules={[{ required: true }]}>
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={[
              { value: 'zero', label: '清零业绩' },
              { value: 'restack', label: '重新核算' },
              { value: 'pin', label: '钉死 L/R' },
            ]}
          />
        </Form.Item>

        {action === 'zero' ? (
          <Form.Item name="zero_scope" label="清零范围" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 'self', label: '仅本账户' },
                { value: 'binary_subtree', label: '整棵二叉子树（含自身）' },
                { value: 'left_leg', label: '仅左区整棵子树' },
                { value: 'right_leg', label: '仅右区整棵子树' },
              ]}
            />
          </Form.Item>
        ) : null}

        {action === 'restack' ? (
          <Form.Item name="preserve_pinned" label="保留已钉死节点" rules={[{ required: true }]}>
            <Select
              options={[
                { value: 1, label: '是 — 跳过 note 含 [pin]/平移 的节点' },
                { value: 0, label: '否 — 全部按公式重算' },
              ]}
            />
          </Form.Item>
        ) : null}

        {action === 'pin' ? (
          <>
            <Form.Item
              name="left_display"
              label="左区显示业绩（留空不改）"
              extra="填写表上看到的数字，例如 183"
            >
              <InputNumber min={0} style={{ width: '100%' }} placeholder="L 显示值" />
            </Form.Item>
            <Form.Item name="right_display" label="右区显示业绩（留空不改）">
              <InputNumber min={0} style={{ width: '100%' }} placeholder="R 显示值" />
            </Form.Item>
          </>
        ) : null}

        <Form.Item
          name="confirm_username"
          label="确认用户名（须与目标账户完全一致）"
          rules={[{ required: true }]}
        >
          <Input placeholder={username} />
        </Form.Item>
        <Form.Item name="note" label="操作备注" rules={[{ required: true, min: 3 }]}>
          <Input.TextArea rows={2} placeholder={`业绩操作 ${username}…`} />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={submitting} block>
          预览影响
        </Button>
      </Form>

      {preview ? (
        <div style={{ maxHeight: '50vh', overflow: 'auto' }}>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message={`即将执行：${preview.action} · 影响约 ${preview.affected_count} 项`}
          />
          <Descriptions size="small" bordered column={1}>
            <Descriptions.Item label="目标">
              #{preview.user_id} {preview.username}
            </Descriptions.Item>
            {preview.action === 'zero' ? (
              <Descriptions.Item label="清零范围">{preview.zero_scope}</Descriptions.Item>
            ) : null}
            {preview.sample_users.length > 0 ? (
              <Descriptions.Item label="样本（改前）">
                {preview.sample_users.slice(0, 20).map((u) => (
                  <div key={u.id}>
                    #{u.id} {u.username} team={u.team_sum} my={u.my_sum}
                  </div>
                ))}
                {preview.affected_count > 20 ? (
                  <Typography.Text type="secondary">
                    …共 {preview.affected_count} 个账户
                  </Typography.Text>
                ) : null}
              </Descriptions.Item>
            ) : null}
            {preview.restack ? (
              <Descriptions.Item label="重算明细">
                扫描 {preview.restack.scanned} · 将更新 {preview.restack.updated}
                {preview.restack.changes.map((c) => (
                  <div key={c.id}>
                    #{c.id} {c.username}: {c.before} → {c.after}
                    {c.skipped ? '（跳过）' : ''}
                  </div>
                ))}
              </Descriptions.Item>
            ) : null}
            {preview.pin ? (
              <>
                <Descriptions.Item label="左区">
                  {preview.pin.left.child_username
                    ? `#${preview.pin.left.child_id} ${preview.pin.left.child_username}: ${preview.pin.left.before_display} → ${preview.pin.left.after_display ?? '不变'}`
                    : '无直挂'}
                </Descriptions.Item>
                <Descriptions.Item label="右区">
                  {preview.pin.right.child_username
                    ? `#${preview.pin.right.child_id} ${preview.pin.right.child_username}: ${preview.pin.right.before_display} → ${preview.pin.right.after_display ?? '不变'}`
                    : '无直挂'}
                </Descriptions.Item>
              </>
            ) : null}
            <Descriptions.Item label="备注">{preview.note}</Descriptions.Item>
          </Descriptions>
          <Space style={{ marginTop: 12 }}>
            <Button onClick={() => { setPreview(null); setPending(null); }}>取消</Button>
            <Button type="primary" danger loading={confirming} onClick={handleConfirm}>
              确认执行
            </Button>
          </Space>
        </div>
      ) : null}
    </Space>
  );
}
